import { css, html, LitElement } from 'lit';
import type { Category } from './entities';
import { customElement, property, state } from 'lit/decorators.js';

@customElement('umb-market-category')
export class UmbMarketCategory extends LitElement {
  @property({ type: Object })
  value?: Category;

  @property({ type: String, attribute: 'tooltip-position' })
  tooltipPosition: 'left' | 'right' = 'left';

  @property({ type: Boolean, reflect: true })
  active = false;

  @state()
  private _mouseover = false;

  #setHover() {
    this._mouseover = true;
  }

  #clearHover() {
    this._mouseover = false;
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('popstate', this.#setActive);
    this.#setActive();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('popstate', this.#setActive);
  }

  #setActive = () => {
    this.value!.active = window.location.pathname === this.value?.link;
    this.active = this.value!.active;
  };

  render() {
    if (!this.value) return null;

    return html`<a
      href=${this.value.link}
      @mouseover=${this.#setHover}
      @mouseleave=${this.#clearHover}
    >
      <div>${this.value.icon} ${this.value.icon}</div>
      <span>${this.value.name}</span>
      <umb-market-tooltip
        .text=${this.value.tooltip}
        ?in=${this._mouseover}
        x=${this.tooltipPosition}
      ></umb-market-tooltip>
    </a>`;
  }

  static styles = css`
    :host {
      position: relative;
      width: 100%;
    }

    :host-context(.in) umb-market-tooltip {
      display: none !important;
    }

    umb-market-tooltip {
      position: absolute;
      inset: 0;
      pointer-events: none;
    }

    a {
      text-decoration: none;
      box-sizing: border-box;
      display: flex;
      align-items: center;
      padding: var(--xs-spacer);
    }

    div {
      height: var(--xl-spacer);
      width: var(--xxl-spacer);
      display: flex;
      flex: 0;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      position: relative;
    }

    a span {
      color: var(--uui-color-default);
      font-weight: 400;
    }

    svg {
      transition: stroke ease-in-out 0.3s;
      width: 44px;
      height: 44px;
    }

    svg:first-of-type {
      position: relative;
      z-index: 2;
    }

    svg:nth-of-type(2) {
      --stroke: transparent;
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translateY(calc(-50% + 3px)) translateX(calc(-50% + 3px));
      z-index: 1;
    }

    :host(:hover) svg:nth-of-type(2),
    :host([active]) svg:nth-of-type(2) {
      --stroke: var(--uui-color-current);
    }

    :host(:hover),
    :host([active]) {
      border-radius: var(--xs-spacer);
      box-shadow: 0px 19px 28px 0px #0000000d;
    }

    :host([active]) {
      background: white;
    }

    span {
      display: block;
      position: relative;
      font-size: 18px;
      line-height: normal;
      font-weight: 400;
      color: var(--uui-color-text);
      margin-left: var(--s-spacer);
    }

    @media (min-width: 500px) {
      :host {
        width: 400px;
      }
    }

    @media (min-width: 992px) {
      :host {
        display: block;
        width: 115px;
        transition: background ease-in-out 0.2s;
      }

      a {
        display: block;
        padding: var(--s-spacer);
        text-align: center;
        margin: unset;
      }

      div {
        margin: 0 auto;
      }

      span {
        margin-top: 12px;
        margin-left: 0;
        font-size: 14px;
        font-weight: 300;
      }
    }

    @media (min-width: 1450px) {
      :host {
        width: 132px;
      }

      span {
        font-size: 16px;
        font-weight: 300;
      }
    }
  `;
}
